import React, { useState, useCallback, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { EyeIcon } from '@heroicons/react/24/outline';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from './Button';

interface ContactInfoRevealProps {
  listingId: string;
  showAddress: string;
  showSupportPhone: string;
  onReveal: (info: ContactInfo) => void;
}

interface ContactInfo {
  address?: string;
  latitude?: number;
  longitude?: number;
  supportPhone?: string;
}

const ContactInfoReveal: React.FC<ContactInfoRevealProps> = ({
  listingId,
  showAddress,
  showSupportPhone,
  onReveal,
}) => {
  const [isRevealing, setIsRevealing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const shouldShowAddress = showAddress === 'captcha' || showAddress === 'always';
  const shouldShowPhone = showSupportPhone === 'everyone';

  // Called when reCAPTCHA completes (either invisibly or after challenge)
  const handleRecaptchaChange = useCallback(async (token: string | null) => {
    if (!token) {
      setIsRevealing(false);
      return;
    }

    try {
      // Call API with the token
      const response = await api.post<ContactInfo>(`/listings/${listingId}/contact-info`, {
        recaptchaToken: token,
      });

      setContactInfo(response);
      setRevealed(true);
      onReveal(response);
      toast.success('Contact information revealed');
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      toast.error(axiosError?.response?.data?.error || 'Failed to reveal contact information');
    } finally {
      setIsRevealing(false);
      // Reset reCAPTCHA for potential future use
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  }, [listingId, onReveal]);

  const handleReveal = useCallback(async () => {
    setIsRevealing(true);
    
    // Execute invisible reCAPTCHA - this will trigger handleRecaptchaChange
    // with the token when verification completes (may show a challenge if needed)
    if (recaptchaRef.current) {
      recaptchaRef.current.execute();
    }
  }, []);

  // Early return after all hooks have been called
  if (!shouldShowAddress && !shouldShowPhone) {
    return null;
  }

  if (revealed && contactInfo) {
    return (
      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">Contact Information</h3>
            <div className="mt-2 text-sm text-green-700 space-y-1">
              {contactInfo.address && (
                <p>
                  <span className="font-semibold">Address:</span> {contactInfo.address}
                </p>
              )}
              {contactInfo.supportPhone && (
                <p>
                  <span className="font-semibold">Phone:</span> {contactInfo.supportPhone}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2 || ''}
        size="invisible"
        onChange={handleRecaptchaChange}
      />

      <div>
        <Button
          onClick={handleReveal}
          isLoading={isRevealing}
          size="sm"
        >
          <EyeIcon className="h-5 w-5 mr-2 inline-block" />
          Click to reveal
        </Button>
      </div>
    </div>
  );
};

export default ContactInfoReveal;
